import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ResPillComponent } from '../res-pill/res-pill.component';
import { RunnerRenameDialogComponent } from '../runner-rename-dialog/runner-rename-dialog.component';
import { RunnerPairingService, type PairedRunner, type RunnerCurrentJob } from '../../services/runner-pairing.service';

/** Dieselbe Regel wie im Server (NEX-1256): Kleinbuchstaben, Ziffern, Bindestrich, 1–64 Zeichen. */
const LABEL_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/u;
const LABELS_MAX = 20;

export type LabelProblem = 'invalid' | 'duplicate' | 'tooMany';

export type RunnerState = 'disconnected' | 'online' | 'working' | 'offline';

/**
 * Getrennt geht vor allem: Ein getrennter Rechner nimmt nichts mehr an. Offline geht vor Arbeitet:
 * Ein Auftrag, der auf einem ausgeschalteten Rechner steht, läuft nicht.
 */
export function runnerState(runner: PairedRunner): RunnerState {
  if (!runner.connected) {
    return 'disconnected';
  }
  if (!runner.online) {
    return 'offline';
  }
  return runner.currentJob ? 'working' : 'online';
}

/**
 * Liste verbundener Rechner mit Trennen und Etiketten (NEX-1729, aus `runner-pairing-section`
 * verschoben). Laden ist Sache der Seite; Trennen und Etiketten ändern die Liste hier sofort und
 * melden das Ergebnis über `changed`, damit die Seite ihren Stand nachzieht.
 *
 * **Ohne Design-Entwurf**, wie zuvor auf der Kontoseite: im Stil der umgebenden Seite.
 */
@Component({
  selector: 'app-runner-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConfirmDialogComponent,
    DatePipe,
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    RouterLink,
    ResPillComponent,
    RunnerRenameDialogComponent,
  ],
  templateUrl: './runner-list.component.html',
  styleUrl: './runner-list.component.scss',
})
export class RunnerListComponent {
  private readonly service = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  readonly runners = input.required<PairedRunner[]>();
  /** Zustand, Engine, Version, laufender Auftrag und Warteschlange je Zeile (/runners, NEX-1730). */
  readonly showDetails = input(false);

  protected readonly runnerState = runnerState;
  readonly changed = output<PairedRunner[]>();
  /**
   * Nach dem Trennen: Der Rechner ist nicht weg, nur getrennt. Ob er als „Getrennt“ wieder
   * erscheint, weiß nur die Seite, die deshalb neu laden kann (NEX-1734).
   */
  readonly reloadRequested = output<void>();

  /** Eigene Kopie, damit Trennen und Etiketten sofort wirken; eine neue Eingabe gilt trotzdem. */
  readonly connected = linkedSignal(() => this.runners());
  readonly error = signal<string | null>(null);
  readonly disconnecting = signal<string | null>(null);
  /**
   * Trennen ist nicht zurueckzunehmen: Der Rechner bekommt seinen Zugang nur wieder, wenn jemand
   * an ihm sitzt und neu koppelt. Die Knoepfe stehen zudem zeilenweise untereinander — ein Klick
   * daneben trifft den Nachbarn. Deshalb eine Rueckfrage mit Namen darin.
   */
  readonly pendingDisconnect = signal<PairedRunner | null>(null);
  /** Satz zu wartenden Aufträgen in der Trennen-Rückfrage; `null` ohne wartende. */
  readonly disconnectQueuedKey = computed(() => {
    const count = this.pendingDisconnect()?.queuedCount ?? 0;
    if (count === 0) {
      return null;
    }
    return count === 1
      ? 'runnerPairing.connectedRunners.confirmQueuedOne'
      : 'runnerPairing.connectedRunners.confirmQueuedMany';
  });

  /** Hängt am Rechner, nicht an der Zeile: Eine neue Liste im Takt schließt den Dialog nicht. */
  readonly pendingRename = signal<PairedRunner | null>(null);
  readonly pendingRemove = signal<PairedRunner | null>(null);
  readonly removing = signal(false);
  /** Übersetzungsschlüssel der Meldung im Entfernen-Dialog. */
  readonly removeError = signal<string | null>(null);

  /** Eingabe „Etikett hinzufügen" je Rechner; bleibt nach einem Fehler stehen. */
  readonly labelDrafts = signal<Readonly<Record<string, string>>>({});
  /** Rechner, dessen Etiketten gerade gespeichert werden — seine Zeile ist so lange gesperrt. */
  readonly savingLabels = signal<string | null>(null);
  /** Rechner, dessen letztes Speichern fehlschlug; die Meldung steht in seiner Zeile. */
  readonly labelError = signal<string | null>(null);

  protected jobLabel(job: RunnerCurrentJob): string {
    return [job.testRunKey, job.testCaseTitle].filter(Boolean).join(' · ');
  }

  askDisconnect(runner: PairedRunner): void {
    this.pendingDisconnect.set(runner);
  }

  confirmDisconnect(): void {
    const runner = this.pendingDisconnect();
    if (!runner) {
      return;
    }
    this.disconnecting.set(runner.id);
    this.error.set(null);
    this.service
      .disconnect(runner.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.connected.update((runners) => runners.filter((each) => each.id !== runner.id));
          this.disconnecting.set(null);
          this.pendingDisconnect.set(null);
          this.changed.emit(this.connected());
          this.reloadRequested.emit();
        },
        error: () => {
          this.error.set('runnerPairing.error.disconnectFailed');
          this.disconnecting.set(null);
          this.pendingDisconnect.set(null);
        },
      });
  }

  onRenamed(updated: PairedRunner): void {
    this.connected.update((runners) => runners.map((each) => (each.id === updated.id ? updated : each)));
    this.pendingRename.set(null);
    this.changed.emit(this.connected());
  }

  askRemove(runner: PairedRunner): void {
    this.removeError.set(null);
    this.pendingRemove.set(runner);
  }

  cancelRemove(): void {
    this.pendingRemove.set(null);
    this.removeError.set(null);
  }

  confirmRemove(): void {
    const runner = this.pendingRemove();
    if (!runner || this.removing()) {
      return;
    }
    this.removing.set(true);
    this.removeError.set(null);
    this.service
      .removeRunner(runner.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.connected.update((runners) => runners.filter((each) => each.id !== runner.id));
          this.removing.set(false);
          this.pendingRemove.set(null);
          this.changed.emit(this.connected());
        },
        error: (err: unknown) => {
          this.removing.set(false);
          this.removeError.set(removeErrorKey(err));
        },
      });
  }

  labelDraft(runnerId: string): string {
    return this.labelDrafts()[runnerId] ?? '';
  }

  setLabelDraft(runnerId: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.labelDrafts.update((drafts) => ({ ...drafts, [runnerId]: value }));
  }

  /** Warum die Eingabe nicht hinzugefügt werden kann; `null` bei leerer oder gültiger Eingabe. */
  labelProblem(runner: PairedRunner): LabelProblem | null {
    const label = normalizeLabel(this.labelDraft(runner.id));
    if (label === '') {
      return null;
    }
    if (!LABEL_PATTERN.test(label)) {
      return 'invalid';
    }
    if (runner.managedLabels.includes(label)) {
      return 'duplicate';
    }
    return runner.managedLabels.length >= LABELS_MAX ? 'tooMany' : null;
  }

  canAddLabel(runner: PairedRunner): boolean {
    return (
      normalizeLabel(this.labelDraft(runner.id)) !== '' &&
      this.labelProblem(runner) === null &&
      this.savingLabels() === null
    );
  }

  addLabel(runner: PairedRunner): void {
    if (!this.canAddLabel(runner)) {
      return;
    }
    const label = normalizeLabel(this.labelDraft(runner.id));
    this.saveLabels(runner, [...runner.managedLabels, label].sort());
  }

  removeLabel(runner: PairedRunner, label: string): void {
    if (this.savingLabels() !== null) {
      return;
    }
    // Der Knopf verschwindet mit der Marke; ohne neues Ziel faellt der Fokus auf body.
    this.saveLabels(
      runner,
      runner.managedLabels.filter((each) => each !== label),
      () => this.focusLabelInput(runner.id),
    );
  }

  /** Der Server ersetzt die ganze gepflegte Menge; übernommen wird seine Antwort, nicht die eigene Annahme. */
  private saveLabels(runner: PairedRunner, labels: string[], afterSave?: () => void): void {
    this.savingLabels.set(runner.id);
    this.labelError.set(null);
    this.service
      .updateLabels(runner.id, labels)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.connected.update((runners) => runners.map((each) => (each.id === updated.id ? updated : each)));
          this.labelDrafts.update((drafts) => ({ ...drafts, [runner.id]: '' }));
          this.savingLabels.set(null);
          this.changed.emit(this.connected());
          afterSave?.();
        },
        error: () => {
          this.labelError.set(runner.id);
          this.savingLabels.set(null);
        },
      });
  }

  private focusLabelInput(runnerId: string): void {
    afterNextRender(
      () =>
        this.host.nativeElement
          .querySelector<HTMLInputElement>(`[data-testid="runner-label-input"][data-runner-id="${runnerId}"]`)
          ?.focus(),
      { injector: this.injector },
    );
  }
}

/** Am Code unterschieden, nicht am Status, wie RUNNER_OFFLINE im Start-Dialog. */
function removeErrorKey(err: unknown): string {
  const code = err instanceof HttpErrorResponse ? err.error?.code : null;
  if (code === 'RUNNER_STILL_CONNECTED') {
    return 'runners.remove.stillConnected';
  }
  return code === 'RUNNER_JOB_OPEN' ? 'runners.remove.jobOpen' : 'runners.remove.failed';
}

function normalizeLabel(raw: string): string {
  return raw.trim().toLowerCase();
}
